import { useMemo } from 'react';
import { Select } from '../ui/Select';
import { useFontCatalog } from './fontCatalog';

/**
 * The two typography fields: family and weight.
 *
 * Both were free-text `<Input>`s. The family one carried a native `<datalist>`, which is drawn by
 * the OPERATING SYSTEM — a black popup on Windows that matched nothing else in the app — and could
 * only offer the five families the design server happens to have installed. Now both are the app's
 * own {@link Select}: same widget as every other dropdown here, with the full Google catalog behind
 * a search box.
 */

/* -------------------------------------------------------------------------- */
/* Family                                                                     */
/* -------------------------------------------------------------------------- */

export function FontFamilySelect({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  disabled?: boolean;
  onChange: (family: string) => void;
}) {
  const { families } = useFontCatalog();

  /**
   * The list, with the current value guaranteed to be in it.
   *
   * A licensed brand font is not in the Google catalog, and a Select whose value matches no option
   * shows its placeholder — so without this, opening the page on a workspace using "Gotham" would
   * look like the field had been cleared.
   */
  const options = useMemo(() => {
    const v = value.trim();
    if (!v) return families;
    return families.some((f) => f.toLowerCase() === v.toLowerCase()) ? families : [v, ...families];
  }, [families, value]);

  /**
   * Memoized SEPARATELY from the render.
   *
   * Select parses its `<option>` children through a `useMemo` keyed on the children array, so a
   * fresh array each render re-parses all ~1900 of them on every keystroke, in all three fields.
   * Holding the array identity stable is what keeps typing in the search box smooth.
   */
  const children = useMemo(
    () =>
      options.map((f) => (
        <option key={f} value={f}>
          {f}
        </option>
      )),
    [options],
  );

  return (
    <Select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Inter"
      searchable
      searchPlaceholder="Search fonts…"
      // A brand font under licence will never be in this list and must still be nameable — that is
      // why this field was free text before, and none of that intent is given up by the dropdown.
      allowCustomValue
    >
      {children}
    </Select>
  );
}

/* -------------------------------------------------------------------------- */
/* Weight                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The nine CSS weights, named.
 *
 * Stored as the label — "Regular (400)" — which is the format already in every workspace and in
 * `brandAssetsDefaults`. Nothing in the app parses this string (it is displayed in the brand
 * bundle's fonts.txt and nowhere else), so keeping the existing shape avoids rewriting stored data
 * to gain nothing.
 */
export const FONT_WEIGHTS = [
  'Thin (100)',
  'Extra-Light (200)',
  'Light (300)',
  'Regular (400)',
  'Medium (500)',
  'Semi-Bold (600)',
  'Bold (700)',
  'Extra-Bold (800)',
  'Black (900)',
] as const;

export function FontWeightSelect({
  id,
  value,
  disabled,
  onChange,
}: {
  id: string;
  value: string;
  disabled?: boolean;
  onChange: (weight: string) => void;
}) {
  /**
   * Anything already stored that isn't one of the nine — "Bold", "600", an em dash, whatever an
   * older editor or the brand-guidelines auto-fill wrote — is kept as its own option.
   *
   * Deliberately NOT normalised into state on mount: the workspace form's dirty flag is a
   * stringify diff against what was loaded, so silently rewriting a value would mark every legacy
   * workspace as edited the moment it was opened, and the first Save would overwrite it without
   * anyone choosing to. It stays exactly as stored until the user picks something else.
   */
  const legacy = value && !FONT_WEIGHTS.some((w) => w === value) ? value : null;

  return (
    <Select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Regular (400)"
    >
      {legacy && <option value={legacy}>{legacy}</option>}
      {FONT_WEIGHTS.map((w) => (
        <option key={w} value={w}>
          {w}
        </option>
      ))}
    </Select>
  );
}
